import React, { useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Input,
    FormGroup,
    Label,
    Button,
    Spinner,
    Alert
} from "reactstrap";
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import createEpisode from "common/realBackend/blog/createEpisode";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";

const EpisodesCreate = () => {
    const { bookId } = useParams(); // Get the bookId from the URL
    document.title ="Create Episode | AAOOA Admin" ;

    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [storyTextEditorState, setStoryTextEditorState] = useState(EditorState.createEmpty());

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Story Title is required"),
        duration : Yup.number().required("Duration is required"),
        coverImage: Yup.mixed().required("Cover Image is required"),
        audioLink: Yup.mixed().nullable(),
        videoLinkString : Yup.string().nullable()
    });

    const handleEditorChange = (state, setFieldValue, fieldName) => {
        const htmlContent = draftToHtml(convertToRaw(state.getCurrentContent()));

        setFieldValue(fieldName, htmlContent);
        setStoryTextEditorState(state);
 
    };

    const handleSubmit = async (values, { resetForm }) => {
        console.log("Form values before submission:", values); // Log form data

        const { title, duration, storyText, coverImage, audioLink, videoLinkString } = values;
        setLoading(true);
        try {
            await createEpisode(bookId, title,storyText, duration, coverImage, audioLink, videoLinkString);
            setAlertMessage("Episode created successfully!");

            setAlertVisible(true);
            resetForm();
            setCoverImagePreview(null);
            setStoryTextEditorState(EditorState.createEmpty());
        } catch (error) {
            console.error("Error creating:", error);
            setAlertMessage("Error creating. Please try again.");
            setAlertVisible(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Stories" breadcrumbItem={bookId ? "Create Episode" : "Create Story"} />

                {alertVisible && (
                    <Alert color={alertMessage.includes("Error") ? "danger" : "success"}
                        toggle={() => setAlertVisible(false)}
                        style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1050 }}>
                        {alertMessage}
                    </Alert>
                )}

                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                                <CardTitle className="mb-4">{bookId ? "Create New Episode" : "Create New Story"}</CardTitle>
                                <Formik
                                    initialValues={{
                                        title: "",
                                        coverImage: null,
                                        duration : null,
                                        videoLinkString : "",
                                        audioLink : null
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ setFieldValue }) => (
                                        <Form>
                                            <FormGroup className="mb-4">
                                                <Label htmlFor="title" className="col-form-label">Episode Title</Label>
                                                <Field
                                                    id="title"
                                                    name="title"
                                                    as={Input}
                                                    placeholder="Enter Episode Title..."
                                                />
                                                <ErrorMessage name="title" component="div" className="text-danger" />
                                            </FormGroup>

                                            <FormGroup className="mb-4">
                                                <Label htmlFor="title" className="col-form-label">Episode Duration</Label>
                                                <Field
                                                    id="duration"
                                                    name="duration"
                                                    as={Input}
                                                    placeholder="Enter Episode Duration..."
                                                />
                                                <ErrorMessage name="duration" component="div" className="text-danger" />
                                            </FormGroup>


                                            <FormGroup className="mb-4">
                                                <Label className="col-form-label">Content</Label>
                                                <Editor
                                                    editorState={storyTextEditorState}
                                                    toolbarClassName="toolbarClassName"
                                                    wrapperClassName="wrapperClassName"
                                                    editorClassName="editorClassName"
                                                    placeholder="Write the main story content here..."
                                                    onEditorStateChange={(state) => handleEditorChange(state, setFieldValue, "storyText")}
                                                />
                                                <ErrorMessage name="storyText" component="div" className="text-danger" />
                                            </FormGroup>

                                            <FormGroup className="mb-4">
                                                <Label className="col-form-label">Cover Image</Label>
                                                <Input
                                                    type="file"
                                                    className="form-control"
                                                    onChange={(e) => {
                                                        const file = e.currentTarget.files[0];
                                                        setFieldValue("coverImage", file);
                                                        setCoverImagePreview(URL.createObjectURL(file));
                                                    }}
                                                />
                                                {coverImagePreview && (
                                                    <div className="mt-2">
                                                        <img src={coverImagePreview} alt="Cover Preview" style={{ maxWidth: '250px', height: 'auto' }} />
                                                    </div>
                                                )}
                                                <ErrorMessage name="coverImage" component="div" className="text-danger" />
                                            </FormGroup>

                                            <FormGroup className="mb-4">
                                                <Label className="col-form-label">Upload Audio</Label>
                                                <Input
                                                    type="file"
                                                    className="form-control"
                                                    onChange={(e) => setFieldValue("audioLink", e.currentTarget.files[0])}
                                                />
                                            </FormGroup>

                                            <FormGroup className="mb-4">
                                                <Label htmlFor="videoLinkString" className="col-form-label">Video Link</Label>
                                                <Field
                                                    id="videoLinkString"
                                                    name="videoLinkString"
                                                    as={Input}
                                                    placeholder="Enter Video Link..."
                                                />
                                            </FormGroup>

                                            <Row className="justify-content-end">
                                                <Col lg="10">
                                                    <Button type="submit" color="primary" disabled={loading}>
                                                        {loading ? <Spinner size="sm" /> : "Create Episode"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default EpisodesCreate;
